import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { AgentResponseSchema } from './ai/models.ai_agent';
import { AiStatusLogSchema } from './ai/models.ai_prediction_logs';
import {
  ProviderIdSchema,
  ProviderOperationIdsSchema,
} from './integrations/integration.models.operations';
import {
  DateTimeTzSchema,
  GenericConnectionId,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GenericUserID,
  GenericUUID,
  YYYYMMDDSchema,
} from './models.generic';
import { PresentableNotificationSchema } from './models.notifications';

extendZodWithOpenApi(z);

// NB: Same as AI API, except always a string. Re-visit to trust/guard AI undefined's better and kill this.
const GeneratedNoteSchema = z.object({
  with_reference: z.coerce.string().default(''),
  without_reference: z.coerce.string().default(''),
});

/**
 * All possible Pusher event/messages that TIC can send.
 * Payloads are Zod parsed and validated.
 */
export const PusherEventPayloadSchemas = {
  ai_status_update: AiStatusLogSchema.extend({
    range_from: DateTimeTzSchema,
    range_to: DateTimeTzSchema,
    account_id: GenericTimelyAccountId,
    infered_dates: z
      .array(YYYYMMDDSchema)
      .describe('The YYYY-MM-DD dates of relevant, ref Timelys ?date control'),
  }).or(AiStatusLogSchema),
  ai_agent_response: AgentResponseSchema,
  new_notification: z.object({
    message: z.string(),
    notification: PresentableNotificationSchema.nullable(),
  }),
  /**
   * @todo consider sending the memories directly in the payload to drastically reduce load.
   * @link https://linear.app/timelyapp/issue/IDE-154/tic-optimization-when-pusher-updates-come-through-maybe-load-partial */
  new_memories: z.object({
    memory_ids: z.array(z.number().int().describe('All changed or new memories')),
    range_from: DateTimeTzSchema,
    range_to: DateTimeTzSchema,
  }),
  userJoinedTic: z.object({
    ticUserId: GenericUserID.optional(),
    timelyUserId: GenericTimelyUserId.optional(),
  }),
  userLeftTic: z.any(),

  /** Note generated for hour */
  hour_note_generation: z.object({
    hour_id: z.number().optional().describe('ID of the hour (only after draft has saved'),
    note: GeneratedNoteSchema,
    error: z.string().optional(),
    updated_at: DateTimeTzSchema,
  }),
  /** Note generated for suggestion */
  suggestion_note_update: z.object({
    suggestion_id: z.string().uuid(),
    note: GeneratedNoteSchema,
    error: z.string().optional(),
    updated_at: DateTimeTzSchema,
  }),
  /* Single event for all integration operation job status updates */
  integration_job_event: z.object({
    tool_id: ProviderIdSchema,
    connection_id: GenericConnectionId,
    requested_operation_id: ProviderOperationIdsSchema.describe('The operation ID requested'),
    job_id: z.string().describe('Unique ID for tracking this operation job execution'),
    status: z.enum(['started', 'completed', 'failed']).describe('Current status of the operation'),
    executed_operation_ids: z
      .array(ProviderOperationIdsSchema)
      .optional()
      .describe(
        'The "unwrapped" operation IDs, e.g if `synchronize` is requested, all the operations with `runOnSynchronize` are listed here (only present on completed)'
      ),
    error: z.string().optional().describe('Error message (only present on failed)'),
    message: z.string().optional().describe('Status message for UI display'),
  }),

  /** Timesheet proposal events */
  // The Rails channel always includes proposal_id/error_message/duration_seconds in the
  // hash, defaulting to nil — Ruby serializes a nil hash value as JSON `null` (no `.compact`),
  // so the wire payload carries `null` rather than absent on the failure/skip paths. `.optional()`
  // rejects `null`, so the three nil-capable fields must be `.nullish()` (= optional + nullable)
  // to validate the real emitted shape. `success` is always a boolean; `generation_mode` is
  // omitted entirely (never null) so it stays `.optional()`.
  timesheet_proposal_generation_completed: z.object({
    proposal_id: GenericUUID.nullish(),
    success: z.boolean(),
    error_message: z.string().nullish(),
    duration_seconds: z.number().min(0).nullish(),
    generation_mode: z.enum(['manual', 'auto']).optional(),
    // Date is present on Rails completion payloads, but remains nullable for
    // older or pre-attempt payloads. The frontend uses it to clear one day's
    // generation state instead of falling back to clearing every pending day.
    date: YYYYMMDDSchema.nullish().describe('YYYY-MM-DD date of the timesheet proposal'),
    // Rails emits the matched TimesheetGenerationRequest id when the engine echoed one. This is
    // diagnostic/correlation data only: the FE logs it to scoped debug output but does not expose it
    // in the visible toast UI.
    generation_request_id: z.string().nullish(),
  }),

  /** Timesheet proposal note regeneration completed */
  // Same nil-bearing reality as generation above: the Rails channel always includes
  // proposal_id/date/error_message, nil on failure/skip → JSON `null` on the wire, so those three
  // are `.nullish()`. entry_ids defaults to `[]` (never null) so it stays `.optional()`.
  timesheet_proposal_notes_regenerated: z.object({
    proposal_id: GenericUUID.nullish(),
    // Rails timesheet_proposal_entries use bigint PKs, so Rails-channel entry_ids are
    // stringified bigints; type them z.string() (not z.string().uuid(), which would reject
    // '101') to validate that stringified-bigint reality, while staying backward-compatible
    // (a uuid is still a valid string).
    entry_ids: z.array(z.string()).optional(),
    date: YYYYMMDDSchema.nullish().describe('YYYY-MM-DD date of the timesheet proposal'),
    success: z.boolean(),
    error_message: z.string().nullish(),
  }),
};

export const PusherEventNamesSchema = z
  .enum(Object.keys(PusherEventPayloadSchemas) as [keyof typeof PusherEventPayloadSchemas])
  .openapi({
    example: 'new_memories',
    description: 'Names of all allowed pusher events from TIC',
  });

export type PusherEventNames = z.infer<typeof PusherEventNamesSchema>;

export type PusherEventPayloadMap = {
  [K in keyof typeof PusherEventPayloadSchemas]: z.infer<(typeof PusherEventPayloadSchemas)[K]>;
};

// This type represents all possible payloads we can send as pusher messages
// export type AllTicPusherMessages = PusherEventPayloadMap[keyof PusherEventPayloadMap];

/** Channel definitions */
export type PusherChannelScope = 'private' | 'account';
export type PusherChannelValue = `private-${string | number}` | `account-${string | number}`;

export type TriggerPusherEventParams<T extends keyof PusherEventPayloadMap> = {
  /** DON'T echo the event back to user belonging to this socketId */
  socketId: string | null;
  /** Name of the Pusher event */
  event: T;
  /** Dispatch to private or account channel? Requires the TIC or TImely user/account.id  */
  channel: PusherChannelScope;
  /** The message to dispatch */
  payload: PusherEventPayloadMap[T];
  /** TIC user.id */
  userId?: string;
  /** TIC account.id */
  accountId?: string;
  /** Timely user.id */
  timelyUserId?: number;
  /** Timely account.id */
  timelyAccountId?: number;
};
